import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth";
import { useBranding } from "../context/BrandingContext";
import { BrandIcon, Wordmark } from "../components/Brand";
import { Button, Input, Field } from "../components/common";
import { formatApiError } from "../api";

// Baked in at frontend build time (see frontend/Dockerfile + docker-compose.yml).
// Blank means Google sign-in wasn't configured for this deployment. This one
// genuinely has to be a build arg -- CRA inlines REACT_APP_* into the bundle --
// which is exactly why *branding* is fetched at runtime instead (see
// context/BrandingContext.jsx): a per-customer bundle is a per-customer fork.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function Login() {
  const { t } = useTranslation();
  const { login, register, loginWithGoogle } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      navigate("/");
    } catch (err) {
      console.error("Auth request failed:", err);
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    const handleCredential = async (response) => {
      setError("");
      try {
        await loginWithGoogle(response.credential);
        navigate("/");
      } catch (err) {
        console.error("Google auth request failed:", err);
        setError(formatApiError(err.response?.data?.detail) || err.message);
      }
    };

    // The GIS script tag (public/index.html) loads async/defer, so it may
    // not be ready yet on first mount -- poll briefly until window.google
    // shows up rather than assuming load order.
    const tryInit = () => {
      if (cancelled) return;
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          // Narrows Google's account picker only. The enforcement boundary is
          // GOOGLE_WORKSPACE_DOMAIN, re-checked server-side in
          // auth_router.py against the token's own verified claims -- this hint
          // is cosmetic and must never be mistaken for the control.
          ...(branding.google_hd ? { hd: branding.google_hd } : {}),
          callback: handleCredential,
        });
        const el = document.getElementById("google-signin-button");
        if (el) window.google.accounts.id.renderButton(el, { theme: "outline", size: "large", width: 320 });
      } else {
        setTimeout(tryInit, 100);
      }
    };
    tryInit();

    return () => { cancelled = true; };
    // branding.google_hd is stable by the time this mounts (BrandingProvider
    // holds first paint until the record resolves), but it belongs in the deps
    // rather than being read out of a stale closure.
  }, [navigate, loginWithGoogle, branding.google_hd]);

  return (
    <div className="min-h-screen flex bg-bg text-txt">
      {/* Left visual */}
      {/* Artwork panel. With no login_image_url configured (the product
          default) this falls back to a gradient drawn from the instance's own
          brand colours -- deliberately no external image dependency, so a
          fresh install doesn't reach out to a third-party CDN to render its
          login screen. */}
      <div className="hidden lg:flex w-1/2 relative overflow-hidden border-r border-border bg-[#060B19]">
        {branding.login_image_url ? (
          <img
            src={branding.login_image_url}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-primary/10 to-glow/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#060B19] via-[#060B19]/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12">
          <div className="flex items-center gap-3">
            <BrandIcon className="w-12 h-12" />
            <Wordmark
              className="font-display text-5xl font-black tracking-tighter text-white"
              accentClassName="text-glow"
            />
          </div>
          <p className="text-white/70 mt-3 max-w-sm">{t("auth.pitch")}</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <BrandIcon className="w-8 h-8" />
            <Wordmark className="font-display text-3xl font-black tracking-tighter" />
          </div>
          <h1 className="font-display text-3xl font-bold">{t("auth.welcome")}</h1>
          <p className="text-muted text-sm mt-1">
            {t("auth.subtitle", { brand: branding.org_name })}
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <Field label={t("auth.name")}>
                <Input
                  data-testid="auth-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </Field>
            )}
            <Field label={t("auth.email")}>
              <Input
                data-testid="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>
            <Field label={t("auth.password")}>
              <Input
                data-testid="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>

            {error && (
              <div className="text-sm text-danger" data-testid="auth-error">
                {error}
              </div>
            )}

            <Button type="submit" disabled={loading} className="w-full" data-testid="auth-submit">
              {loading ? t("common.loading") : mode === "login" ? t("auth.signIn") : t("auth.signUp")}
            </Button>
          </form>

          {GOOGLE_CLIENT_ID && (
            <div className="w-full mt-3 flex justify-center" data-testid="google-login-btn">
              <div id="google-signin-button" />
            </div>
          )}

          <div className="text-center text-sm text-muted mt-6">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button
              className="text-primary font-medium"
              data-testid="auth-toggle-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
            >
              {mode === "login" ? t("auth.register") : t("auth.login")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
